import { useQuery } from "@tanstack/react-query";

import { api, type ReportScope } from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { Select } from "../../ui/Select";
import { formatMinutes, rangeIsReady, toRangeValue } from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TicketCount } from "../card/TicketCount";
import type { ChartType } from "../card/chart-types";

/**
 * The desk's response times, agent by agent.
 *
 * The counterpart to the pipeline breakdown beside it: that one asks *where*
 * the desk is slow, this one asks *whose* slow replies those are. Same
 * segments, same arithmetic — `segments.ts` shares the two — so a manager can
 * read one against the other and they cannot come to disagree about what a
 * segment is or what "met" means.
 *
 * ## Attribution is who replied, not who it is assigned to
 *
 * A cycle belongs to the agent who actually answered it. On a shared queue that
 * is regularly not whoever the ticket sits with now, and crediting today's
 * assignee would move history between people every time something is
 * reassigned. Cycles nothing could be attributed to are their own row rather
 * than being dropped, so the table still adds up to what the desk answered.
 *
 * ## Managers only, and on purpose
 *
 * This names individuals and ranks them, which is the one screen the rest of
 * this dashboard deliberately avoids: an agent's own view compares them against
 * a team *aggregate* and never against a named colleague. The server refuses
 * this route to anybody but an admin — the page not rendering it is the
 * courtesy, not the rule.
 *
 * ## What the columns do not say
 *
 * Median alone is not a fair comparison between two people. Somebody on a
 * four-hour pipeline and somebody on a thirty-minute one are keeping different
 * promises; the SLA column accounts for that and the median column does not.
 * The footnote says so, because a table like this gets quoted.
 */

const CHARTS: ChartType[] = ["bar", "stacked-bar", "table"];

const SORTS = [
  { value: "worst-median", label: "Slowest first" },
  { value: "worst-sla", label: "Worst SLA first" },
] as const;

export function AgentSegmentReport({
  layout,
  scope: pageScope,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
}) {
  const stored = layout.card("agent-segments");
  /* The card's own Pipeline and Agent filters, folded into the page's
     scope so they reach both the query and its key. */
  const scope = withCardFilters(pageScope, stored);
  const range = toRangeValue(stored.range, { preset: "this-month" });
  const ready = rangeIsReady(range);
  /* The card's own `metric` slot, as the pipeline breakdown does it -- a
     per-card choice like the chart type, saved with the arrangement. */
  const sort = stored.metric === "worst-sla" ? "worst-sla" : "worst-median";

  const report = useQuery({
    queryKey: queryKeys.dashboardReport("agent-segments", range, scope, {
      sort,
    }),
    queryFn: () => api.agentSegmentReport(range, scope, { sort }),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const rows = report.data?.agents ?? [];
  const series = rows.map((row) => ({ key: row.name, label: row.name }));

  const totalSegments = rows.reduce((sum, row) => sum + row.segments, 0);

  /* Only worth a sentence when there is something in it. A desk where every
     reply is attributed -- the ordinary case -- should not be told about a
     bucket it does not have. */
  const unattributed = rows.find((row) => row.userId === null)?.segments ?? 0;

  return (
    <ReportCard
      id="agent-segments"
      title="Response times by agent"
      subtitle="The same segments as the pipeline breakdown, credited to whoever answered them"
      layout={layout}
      offerAgents={isManager}
      charts={CHARTS}
      series={series}
      actions={
        <Select
          value={sort}
          onChange={(next) => layout.setCard("agent-segments", { metric: next })}
          label="Sort agents by"
          options={SORTS.map((option) => ({ ...option }))}
        />
      }
      footnote={
        <>
          {/*
            The caveat has to travel with the table, because a table like this
            gets screenshotted. Median compares two people answering different
            promises; the SLA column is the one that accounts for the target.
          */}
          <p>
            {totalSegments.toLocaleString()} segments across {rows.length}{" "}
            {rows.length === 1 ? "person" : "people"}, credited to whoever
            replied rather than to whoever the ticket is assigned to now. Median
            is not a like-for-like comparison — a four-hour pipeline and a
            thirty-minute one are different promises, which is what the SLA
            column accounts for and the median column does not.
          </p>
          {unattributed > 0 ? (
            <p>
              {unattributed.toLocaleString()}{" "}
              {unattributed === 1 ? "segment" : "segments"} could not be
              credited to anybody — an automatic reply, or one sent before
              authorship was recorded. Kept in the table so the totals still
              match the desk&rsquo;s.
            </p>
          ) : null}
        </>
      }
    >
      {({ chart, colorFor }) => {
        if (!ready) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              Pick both dates.
            </p>
          );
        }

        if (rows.length === 0) {
          return (
            <p className="py-6 text-center text-xs text-faint">
              {report.isLoading
                ? "Loading…"
                : "Nobody answered anything in this period."}
            </p>
          );
        }

        return (
          <div className="space-y-3">
            <CardChart
              chart={chart}
              rows={rows.map((row) => ({
                label: row.name,
                values: { median: row.medianMinutes },
              }))}
              series={[{ key: "median", label: "Median wait" }]}
              colorBy="row"
              colorFor={colorFor}
              format={formatMinutes}
            />

            {/* The chart carries the median; the table carries what a bar
                height cannot -- the tail, and how much of it was met. Those
                are the numbers a manager acts on. */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-faint">
                    <th className="px-2 py-1.5 font-medium">Agent</th>
                    <th className="px-2 py-1.5 text-right font-medium">Median</th>
                    <th className="px-2 py-1.5 text-right font-medium">p90</th>
                    <th className="px-2 py-1.5 text-right font-medium">p95</th>
                    <th className="px-2 py-1.5 text-right font-medium">SLA met</th>
                    <th className="px-2 py-1.5 text-right font-medium">Replies</th>
                    <th className="px-2 py-1.5 text-right font-medium">Tickets</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.userId ?? "unattributed"}
                      className="border-b border-line/60"
                    >
                      <td className="px-2 py-1.5">
                        <span className="inline-flex items-center gap-1.5">
                          <span
                            aria-hidden
                            className="size-1.5 rounded-full"
                            style={{ backgroundColor: colorFor(row.name) }}
                          />
                          <span
                            className={
                              row.userId === null ? "text-faint" : "text-content"
                            }
                          >
                            {row.name}
                          </span>
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-content">
                        {formatMinutes(row.medianMinutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted">
                        {formatMinutes(row.p90Minutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted">
                        {formatMinutes(row.p95Minutes)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {/* Null is "they only answered untimed work", not a
                            failure and not a perfect score. */}
                        {row.slaRate === null ? (
                          <span
                            title="Nothing they answered had a target"
                            className="text-faint"
                          >
                            —
                          </span>
                        ) : (
                          <span
                            className={
                              row.slaRate >= 0.9
                                ? "text-sla-ok"
                                : row.slaRate >= 0.75
                                  ? "text-sla-warning"
                                  : "text-sla-breached"
                            }
                          >
                            {Math.round(row.slaRate * 100)}%
                          </span>
                        )}
                      </td>
                      {/* Replies, not "segments": the word on a per-person row
                          should be the thing the person did. It is the same
                          number and the same SLA denominator. */}
                      <td className="px-2 py-1.5 text-right tabular-nums text-muted">
                        {row.segments}
                      </td>
                      <td
                        className="px-2 py-1.5 text-right tabular-nums text-muted"
                        title={`${row.segments} repl${row.segments === 1 ? "y" : "ies"} across ${row.tickets} ticket${row.tickets === 1 ? "" : "s"}`}
                      >
                        <TicketCount
                          count={row.tickets}
                          dimension="answered-agent"
                          value={row.userId ? String(row.userId) : ""}
                          label={`${row.name ?? "Unattributed"} — answered in this period`}
                          range={range}
                          scope={scope}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }}
    </ReportCard>
  );
}
